import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/PageTitle/PageTitle";
import ButtonCount from "../../components/ButtonCount/ButtonCount";
import Button from "../../components/Button/Button";
import { useCallback, useEffect, useState } from "react";
import { TableSearch } from "../../components/Table/TableActions";
import Table from "../../components/Table/Table";
import { httprequest } from "../../data/api";
import { ToastContainer, toast } from "react-toastify";
import useStyles from "./styles";
import useWindowSize from "../../hooks/useWindow";
import { Drawer, Pagination } from "@mui/material";
import { Icon } from "@iconify/react";

const REFERRERS_ENDPOINT = "/admin/v1/referrals/referrers";
const PAGE_SIZE = 20;

const getReferrerName = (referrer) => {
  const fullName = [referrer?.first_name, referrer?.last_name].filter(Boolean).join(" ").trim();

  return fullName || "N/A";
};

const AuthenticatedReferrers = () => {
  const classes = useStyles();
  const [searchResult, setSearchResult] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const getReferrers = useCallback((pageNumber = 1, search = "") => {
    setIsFetching(true);
    const params = {
      page: pageNumber,
      limit: PAGE_SIZE
    };

    if (search.trim()) {
      params.search = search.trim();
    }

    httprequest.get(REFERRERS_ENDPOINT, { params }).then((res) => {
      setIsFetching(false);
      const payload = res?.data?.data;
      const rows = payload?.data || [];
      setSearchResult(rows);
      setTotalPages(Number(payload?.pagination?.total_pages) || 1);
      setTotalItems(Number(payload?.pagination?.total_items) || rows.length);
    }).catch((err) => {
      setIsFetching(false);
      toast.error(err?.response?.data?.message || "Failed to fetch authenticated referrers");
    });
  }, []);

  const width = useWindowSize();
  const mobile = width < 600;

  const checkRowSelected = (selected) => {
    if (selected.selectedRows.length > 0) {
      setIsRowSelected(true);
      setSelectedRows(selected.selectedRows);
    } else {
      setIsRowSelected(false);
      setSelectedRows([]);
    }
  };

  const exportToCSV = (exportData, filename) => {
    try {
      if (!exportData?.length) {
        toast.error("No referrers to export");

        return;
      }

      const headers = ["Name", "Email"];

      const escapeCSV = (val) => {
        if (val == null) return "";

        const stringVal = String(val);

        return `"${stringVal.replace(/"/g, '""')}"`;
      };

      const csvRows = [
        headers.join(","),
        ...exportData.map((row) => [
          escapeCSV(getReferrerName(row?.referrer)),
          escapeCSV(row?.referrer?.email || "N/A")
        ].join(","))
      ];

      const csvContent = `\uFEFF${csvRows.join("\n")}`;
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportData.length} records successfully`);
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      const params = {
        page: 1,
        limit: Math.max(totalItems, PAGE_SIZE)
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const res = await httprequest.get(REFERRERS_ENDPOINT, { params });
      const rows = res?.data?.data?.data || [];
      const dateStr = new Date().toISOString().split("T")[0];
      exportToCSV(rows, `authenticated_referrers_${dateStr}.csv`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to export referrers");
    } finally {
      setIsExporting(false);
    }
  };

  const handleViewDetails = (row) => {
    setSelectedRow(row);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedRow(null);
  };

  const columns = [
    {
      name: "First Name",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">First Name</p>}
          {row?.referrer?.first_name || "N/A"}
        </>
      )
    },
    {
      name: "Last Name",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Last Name</p>}
          {row?.referrer?.last_name || "N/A"}
        </>
      )
    },
    {
      name: "Email",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Email</p>}
          {row?.referrer?.email || "N/A"}
        </>
      )
    },
    {
      name: "Phone Number",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Phone Number</p>}
          {row?.referrer?.phone_number || "N/A"}
        </>
      )
    },
    {
      name: "Total Referrals",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Total Referrals</p>}
          {row?.total_referrals ?? 0}
        </>
      )
    },
    {
      name: "Pending",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Pending</p>}
          {row?.pending_referrals ?? 0}
        </>
      )
    },
    {
      name: "Activated",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Activated</p>}
          {row?.activated_referrals ?? 0}
        </>
      )
    },
    {
      name: "Rewarded",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Rewarded</p>}
          {row?.rewarded_referrals ?? 0}
        </>
      )
    },
    {
      name: "",
      center: true,
      sortable: false,
      cell: (row) => (
        <div key={row?.referrer?.referrer_id} className="table-action-container">
          <button onClick={() => handleViewDetails(row)} className="table-action-icon">
            <Icon icon="fluent:eye-16-regular" />
          </button>
        </div>
      )
    }
  ];

  useEffect(() => {
    getReferrers(page, searchTerm);
  }, [getReferrers, page, searchTerm]);

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchTerm(value);
    setPage(1);
  };

  const DrawerContent = () => (
    <div className={classes.drawerContainer}>
      <div className={classes.header}>
        <div className={classes.title}>Referrer Details</div>
        <div onClick={closeDrawer} style={{ cursor: "pointer" }}>
          <Icon icon="line-md:close-circle-filled" width="24" height="24" />
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Referrer Info</div>
        <div className={classes.flexColumn}>
          <div className={classes.gridRow}>
            <div className={classes.label}>First Name:</div>
            <div className={classes.value}>{selectedRow?.referrer?.first_name || "N/A"}</div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>Last Name:</div>
            <div className={classes.value}>{selectedRow?.referrer?.last_name || "N/A"}</div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>Email:</div>
            <div className={classes.value}>{selectedRow?.referrer?.email || "N/A"}</div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>Phone Number:</div>
            <div className={classes.value}>{selectedRow?.referrer?.phone_number || "N/A"}</div>
          </div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Referral Stats</div>
        <div className={classes.flexColumn}>
          <div className={classes.gridRow}>
            <div className={classes.label}>Total Referrals:</div>
            <div className={classes.value}>{selectedRow?.total_referrals ?? 0}</div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>Pending:</div>
            <div className={classes.value}>{selectedRow?.pending_referrals ?? 0}</div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>Activated:</div>
            <div className={classes.value}>{selectedRow?.activated_referrals ?? 0}</div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>Rewarded:</div>
            <div className={classes.value}>{selectedRow?.rewarded_referrals ?? 0}</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout
      children={
        <>
          <PageTitle
            title="Authenticated Referrers"
            subtitle="View authenticated referrers and their referral performance."
          />
          <div className={classes.pageContent}>
            <div className="button-count-container">
              <ButtonCount title="Referrers Count" count={totalItems} />
            </div>
            <Table
              data={searchResult}
              columns={columns}
              subHeader={true}
              pagination={false}
              isLoading={isFetching}
              handleRowSelected={checkRowSelected}
              subHeaderComponent={
                <div className="action_div">
                  <TableSearch placeholder="Search by name or email..." searchTableFunc={handleSearch} />
                  <div style={{ display: "flex", gap: "10px" }}>
                    {isRowSelected && (
                      <Button
                        variant="primaryoutline"
                        buttonSize="fluid"
                        color="btnfontprimary"
                        onClick={() => {
                          const dateStr = new Date().toISOString().split("T")[0];
                          exportToCSV(selectedRows, `authenticated_referrers_selected_${dateStr}.csv`);
                        }}
                        buttonName={`Export Selected (${selectedRows.length})`}
                      />
                    )}
                    <Button
                      variant="primary"
                      buttonSize="fluid"
                      color="btndefault"
                      onClick={handleExportAll}
                      isLoading={isExporting}
                      buttonName="Export"
                    />
                  </div>
                </div>
              }
            />
            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  shape="rounded"
                />
              </div>
            )}
          </div>

          <Drawer anchor="right" open={isDrawerOpen} onClose={closeDrawer}>
            <DrawerContent />
          </Drawer>

          <ToastContainer />
        </>
      }
    />
  );
};

export default AuthenticatedReferrers;
